import { db } from '../firebase';
import {
    collection,
    addDoc,
    query,
    where,
    limit,
    onSnapshot,
    doc,
    updateDoc,
    getDoc,
    getDocs
} from 'firebase/firestore';

/**
 * Booking Service for Customer (fixit) App
 * 
 * Handles creating bookings and listening for status updates.
 * Bookings flow: customer creates → provider sees → provider accepts/rejects
 * 
 * Status lifecycle: pending → accepted → in-progress → completed
 *                   pending → rejected
 *                   pending/accepted → cancelled (by customer)
 */

// ─── CREATE BOOKING ─────────────────────────────────────────────────────────

/**
 * Create a new booking request in Firestore.
 * This will be visible to service providers in real-time.
 */
export async function createBooking({
    customerId,
    customerName,
    customerEmail,
    customerPhone,
    serviceName,
    serviceCategory,
    description,
    address,
    preferredDate,
    preferredTime,
    providerId = null,       // optional: if booking a specific provider
    providerName = null,
    visitFee = 0,
    hourlyRate = 0,
    estimatedPrice = null,
    urgencyMode = null,      // 'immediate' | 'flexible' | null
    customerLatitude = null,
    customerLongitude = null,
    bookingRequestId = null
}) {
    try {
        const bookingData = {
            // Customer info
            customerId,
            customerName,
            customerEmail,
            customerPhone,

            // Service info
            serviceName,
            serviceCategory: serviceCategory || serviceName.toLowerCase().replace(/\s+/g, '-'),
            description: description || '',

            // Location & schedule
            address,
            preferredDate,
            preferredTime,
            customerLatitude,
            customerLongitude,
            urgencyMode,

            // Provider info (null if open request)
            providerId,
            providerName,

            // Pricing
            visitFee,
            hourlyRate,
            estimatedPrice,

            // Status tracking
            status: 'pending',
            statusHistory: [
                {
                    status: 'pending',
                    timestamp: new Date().toISOString(),
                    note: 'Booking request created by customer'
                }
            ],

            // Timestamps
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),

            // Client request fingerprint for timeout recovery.
            bookingRequestId: bookingRequestId || null
        };

        const docRef = await addDoc(collection(db, 'bookings'), bookingData);
        console.log('✅ Booking created with ID:', docRef.id);
        return { id: docRef.id, ...bookingData };
    } catch (error) {
        console.error('❌ Failed to create booking:', error);
        throw error;
    }
}

/**
 * Recover a booking by request id if client timed out while write still succeeded.
 */
export async function getBookingByRequestId(requestId) {
    if (!requestId) return null;

    const q = query(
        collection(db, 'bookings'),
        where('bookingRequestId', '==', requestId),
        limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const found = snapshot.docs[0];
    return { id: found.id, ...found.data() };
}

// ─── FETCH BOOKINGS ─────────────────────────────────────────────────────────

/**
 * Get all bookings for a specific customer (one-time fetch).
 */
export async function getCustomerBookings(customerId) {
    try {
        const q = query(
            collection(db, 'bookings'),
            where('customerId', '==', customerId)
        );
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        return results;
    } catch (error) {
        console.error('❌ Failed to fetch customer bookings:', error);
        throw error;
    }
}

// ─── REAL-TIME LISTENERS ────────────────────────────────────────────────────

/**
 * Listen to all bookings for a customer in real-time.
 * Returns an unsubscribe function.
 */
export function listenToCustomerBookings(customerId, callback) {
    const q = query(
        collection(db, 'bookings'),
        where('customerId', '==', customerId)
    );

    return onSnapshot(q, (snapshot) => {
        const bookings = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        bookings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        callback(bookings);
    }, (error) => {
        console.error('❌ Booking listener error:', error);
        callback([]);
    });
}

// ─── UPDATE BOOKING ─────────────────────────────────────────────────────────

/**
 * Cancel a booking (customer action).
 */
export async function cancelBooking(bookingId) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];

        await updateDoc(bookingRef, {
            status: 'cancelled',
            updatedAt: new Date().toISOString(),
            statusHistory: [
                ...statusHistory,
                {
                    status: 'cancelled',
                    timestamp: new Date().toISOString(),
                    note: 'Cancelled by customer'
                }
            ]
        });

        console.log('✅ Booking cancelled:', bookingId);
    } catch (error) {
        console.error('❌ Failed to cancel booking:', error);
        throw error;
    }
}

/**
 * Confirm payment done (customer action).
 * This is the final step in the booking lifecycle.
 */
export async function confirmPayment(bookingId) {
    try {
        const bookingRef = doc(db, 'bookings', bookingId);
        const bookingSnap = await getDoc(bookingRef);

        if (!bookingSnap.exists()) {
            throw new Error('Booking not found');
        }

        const currentData = bookingSnap.data();
        const statusHistory = currentData.statusHistory || [];

        await updateDoc(bookingRef, {
            status: 'payment-done',
            updatedAt: new Date().toISOString(),
            paidAt: new Date().toISOString(),
            statusHistory: [
                ...statusHistory,
                {
                    status: 'payment-done',
                    timestamp: new Date().toISOString(),
                    note: 'Payment confirmed by customer'
                }
            ]
        });

        console.log('✅ Payment confirmed:', bookingId);
    } catch (error) {
        console.error('❌ Failed to confirm payment:', error);
        throw error;
    }
}

const bookingService = {
    createBooking,
    getBookingByRequestId,
    getCustomerBookings,
    listenToCustomerBookings,
    cancelBooking,
    confirmPayment
};

export default bookingService;
